import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { ServicemeshFullscreenComponent } from '@helpers/components/fullscreen';
import { ServicemeshLoadingBarComponent } from '@helpers/components/loading-bar';
import {
  ServicemeshNavigationService,
  ServicemeshVerticalNavigationComponent,
} from '@helpers/components/navigation';
import { ServicemeshConfigService } from '@helpers/services/config';
import { ServicemeshMediaWatcherService } from '@helpers/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'compact-layout',
  templateUrl: './compact.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    ServicemeshLoadingBarComponent,
    MatButtonModule,
    MatIconModule,
    ServicemeshFullscreenComponent,
    SearchComponent,
    UserComponent,
    RouterOutlet,
    ServicemeshVerticalNavigationComponent,
    MatSlideToggleModule,
  ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);
  private readonly _servicemeshMediaWatcherService = inject(ServicemeshMediaWatcherService);
  private readonly _servicemeshNavigationService = inject(ServicemeshNavigationService);
  private readonly _servicemeshConfigService = inject(ServicemeshConfigService);

  isScreenSmall!: boolean;
  navigation!: Navigation;
  private readonly _unsubscribeAll: Subject<null> = new Subject<null>();

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((navigation: Navigation) => {
        this.navigation = navigation;
      });

    // Subscribe to media changes
    this._servicemeshMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the screen is small
        this.isScreenSmall = !matchingAliases.includes('md');
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._servicemeshNavigationService.getComponent<ServicemeshVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Change the theme Compact or Enterprise
   *
   * @param change
   */
  toggleTheme(change: MatSlideToggleChange): void {
    if (change.checked) {
      this._servicemeshConfigService.config = { layout: 'enterprise' };
    }
  }
}
